import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI } from '../../services/marketplaceAPI';
import DashboardButton from '../../components/DashboardButton';
import { getEmergencyServiceImageUrl } from '../../utils/imageUtils';
import '../../styles/marketplace.css';
import './OrdersPage.css';

const OrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  const [filters, setFilters] = useState({
    status: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({});

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const darkMode = savedTheme === 'dark';
    setIsDarkMode(darkMode);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    const theme = newDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  };

  useEffect(() => {
    fetchOrders();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await orderAPI.getUserOrders(filters);
      
      if (response.success) {
        setOrders(response.data.orders);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to fetch orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Error loading orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({
      ...prev,
      status,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      setDownloadingInvoice(orderId);
      setError('');
      
      await orderAPI.downloadInvoice(orderId);
      
      // Show success message (optional)
      // You could add a toast notification here
      console.log('Invoice downloaded successfully');
      
    } catch (err) {
      console.error('Error downloading invoice:', err);
      setError(`Failed to download invoice for order ${orderId}. Please try again.`);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', color: 'white', icon: '⏳' };
      case 'confirmed':
        return { bg: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)', color: 'white', icon: '✅' };
      case 'processing':
        return { bg: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', color: 'white', icon: '⚡' };
      case 'shipped':
        return { bg: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)', color: 'white', icon: '🚚' };
      case 'delivered':
        return { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', icon: '✨' };
      case 'cancelled':
        return { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white', icon: '❌' };
      default:
        return { bg: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)', color: 'white', icon: '📦' };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`orders-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Dashboard Button */}
      <DashboardButton userRole="Patient" />
      
      {/* Dark Mode Toggle */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle"
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? '☀️' : '🌙'}
      </button>
      
      {/* Header */}
      <div className={`orders-header ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="orders-header-content">
          <h1>
            <span>📦</span>
            My Orders
          </h1>
          <p>Track and manage your orders with ease</p>
        </div>
      </div>

      <div className="orders-container">
        {/* Filters */}
        <div className="filters-section">
          <h3>🔍 Filter Orders</h3>
          <div className="filter-buttons">
            {[
              { value: '', label: 'All Orders', icon: '📋' },
              { value: 'pending', label: 'Pending', icon: '⏳' },
              { value: 'confirmed', label: 'Confirmed', icon: '✅' },
              { value: 'processing', label: 'Processing', icon: '⚡' },
              { value: 'shipped', label: 'Shipped', icon: '🚚' },
              { value: 'delivered', label: 'Delivered', icon: '📦' },
              { value: 'cancelled', label: 'Cancelled', icon: '❌' }
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => handleStatusFilter(filter.value)}
                className={`filter-btn ${filters.status === filter.value ? 'active' : ''}`}
              >
                {filter.icon} {filter.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <div className="orders-grid">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="loading-skeleton order-card">
                <div style={{ height: '1.5rem', background: 'rgba(0,0,0,0.1)', borderRadius: '0.5rem', marginBottom: '1rem', width: '60%' }}></div>
                <div style={{ height: '1rem', background: 'rgba(0,0,0,0.1)', borderRadius: '0.5rem', marginBottom: '0.75rem', width: '40%' }}></div>
                <div style={{ height: '4rem', background: 'rgba(0,0,0,0.1)', borderRadius: '0.75rem' }}></div>
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h3>
              {filters.status ? 
                `No ${filters.status} orders found` : 
                'No orders found'
              }
            </h3>
            <p>Start shopping to see your orders here</p>
            <button
              onClick={() => navigate('/marketplace')}
              className="empty-state-btn"
            >
              🛍️ Start Shopping
            </button>
          </div>
        ) : (
          <>
            {/* Orders Grid */}
            <div className="orders-grid">
              {orders.map((order) => {
                const statusInfo = getStatusColor(order.status);
                return (
                  <div key={order._id} className="order-card">
                    {/* Order Header */}
                    <div className="order-header">
                      <div className="order-header-top">
                        <div>
                          <div className="order-id">🧾 Order #{order.orderId}</div>
                          <div className="order-date">📅 Placed on {formatDate(order.orderDate)}</div>
                        </div>
                      </div>
                      <div className="order-header-bottom">
                        <span 
                          className="order-status"
                          style={{
                            background: statusInfo.bg,
                            color: statusInfo.color
                          }}
                        >
                          {statusInfo.icon} {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                        <div className="order-total">৳{order.totalAmount.toFixed(2)}</div>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="order-items-section">
                      <h4>📦 Order Items</h4>
                      {order.items.slice(0, 2).map((item, index) => (
                        <div key={index} className="order-item">
                          <img
                            src={(() => {
                              const emergencyImageUrl = getEmergencyServiceImageUrl(item.name);
                              if (emergencyImageUrl) {
                                return emergencyImageUrl;
                              }
                              return item.productId?.image 
                                ? (item.productId.image.startsWith('http') 
                                    ? item.productId.image 
                                    : `https://weheal-backend.onrender.com${item.productId.image}`)
                                : '/placeholder-product.png';
                            })()}
                            alt={item.name}
                            className="order-item-image"
                            onError={(e) => {
                              const emergencyImageUrl = getEmergencyServiceImageUrl(item.name);
                              if (!emergencyImageUrl) {
                                e.target.src = '/placeholder-product.png';
                              }
                            }}
                          />
                          <div className="order-item-details">
                            <div className="order-item-name">{item.name}</div>
                            <div className="order-item-meta">
                              <span className="order-item-category">
                                {(() => {
                                  // Check if it's an emergency service
                                  const serviceName = item.name?.toLowerCase() || '';
                                  if (serviceName.includes('wheelchair') || 
                                      serviceName.includes('oxygen') || 
                                      serviceName.includes('cylinder') ||
                                      serviceName.includes('ambulance') ||
                                      serviceName.includes('nurse') ||
                                      serviceName.includes('emergency')) {
                                    return '🏥 Service';
                                  }
                                  return item.category === 'medicine' ? '💊 Medicine' : '🧪 Lab Test';
                                })()}
                              </span>
                              <span>Qty: <strong>{item.quantity}</strong></span>
                              <span>৳{item.price} each</span>
                            </div>
                          </div>
                          <div className="order-item-price">৳{item.total.toFixed(2)}</div>
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <div className="more-items">
                          📦 +{order.items.length - 2} more item(s)
                        </div>
                      )}
                    </div>

                    {/* Order Actions */}
                    <div className="order-actions">
                      <button
                        onClick={() => navigate(`/orders/${order.orderId}`)}
                        className="action-btn action-btn-primary"
                      >
                        👁️ View Details
                      </button>
                      <button
                        onClick={() => handleDownloadInvoice(order.orderId)}
                        disabled={downloadingInvoice === order.orderId}
                        className="action-btn action-btn-secondary"
                        style={{
                          opacity: downloadingInvoice === order.orderId ? 0.7 : 1
                        }}
                      >
                        {downloadingInvoice === order.orderId ? (
                          <>⏳ Downloading...</>
                        ) : (
                          <>📄 Download Invoice</>
                        )}
                      </button>
                      {['pending', 'confirmed'].includes(order.status) && (
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to cancel this order?')) {
                              // Cancel order logic will be added
                            }
                          }}
                          className="action-btn action-btn-secondary"
                        >
                          ❌ Cancel Order
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-content">
                  <div className="pagination-info">
                    📊 Showing <strong>
                      {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
                      {Math.min(pagination.currentPage * filters.limit, pagination.totalOrders)}
                    </strong> of <strong>
                      {pagination.totalOrders}
                    </strong> orders
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="pagination-btn pagination-btn-prev"
                    >
                      ⬅️ Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="pagination-btn pagination-btn-next"
                    >
                      Next ➡️
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
